import type { IData, IRate, IPromo, IPromoGroup } from "~/interfaces";
import { getThemeData } from "~/utils";
const data: IData = getThemeData();

/**
 * Get promo values from json, filtering by current selected credit
 */
export const getPromoValues = (): IPromoGroup | null => {
  let selected: number = getStateValue('credit');
  if (!selected || getStateValue("isRevolving")) selected = getStateValue('selectedCredit');

  if (!data.promo?.values || !selected) return null;

  const promo = data.promo.values.find(
    (value) => value.base?.default?.imp_finanz === selected
  );
  return promo ?? null;
};

export const getPromoDefaultModelFromCredit = (credit: number, rate: number): IRate | null => {
  if (!data.promo?.values) return null;

  const promo = data.promo.values.find(
    (value) => {
      return value.base?.default?.imp_finanz === credit
      && value.base?.default?.rate === rate
    }
  );
  return promo?.base.default ? formatPromo(promo?.base.default) : null;
};

/**
 * Determine if selected credit has a promo model
 * @returns boolean
 */
export const isPromoCreditSelected = (): boolean => {
  return !!getPromoValues()
}

/**
 * Determine if the selected offer is promo
 */
export const isPromoSelected = (): boolean => {
  const promoValues = getPromoValues();
  return !!promoValues?.base.default;
};

/**
 * Determine if the selected credit has an upgrade option
 */
export const hasUpgrade = (): boolean => {
  return !!getPromoValues()?.upgrade?.default;
};

/**
 * Get promo credit value
 */
export function promoCredit(): number | null {
  return getPromoValues()?.base.default.imp_finanz ?? null;
}

/**
 * Format promo
 * @param promo
 */
export const formatPromo = (promo: any) => {
  promo.imposta = parseInt(promo.imp_finanz) * 0.0025;
  if(promo.secci) {
    promo.secci_url = `${data.endpoints.secci}${promo.secci}`;
  } else {
    promo.secci_url = `${data.endpoints.secci}${promo.imp_finanz}_${promo.rate}.pdf`;
  }
  return formatPriceValues(promo);
};

const getPromoWithServices = (modelGroup: IPromo, alwaysSelectClub = false): IRate => {

  const CLUB =
    alwaysSelectClub ||
    (getStateValue("service") && getStateValue("servicePack") === CLUB_PLATINUM.code);
  const INSURANCE_PACKAGE_E =
    getStateValue("insurance") &&
    getStateValue("insurancePack") === INSURANCE_PACKAGES.E;
  const INSURANCE_PACKAGE_G =
    getStateValue("insurance") &&
    getStateValue("insurancePack") === INSURANCE_PACKAGES.G;

  let promo: IRate = modelGroup.default;
  if (CLUB) promo = modelGroup.club;
  if (INSURANCE_PACKAGE_E) promo = modelGroup.packE;
  if (CLUB && INSURANCE_PACKAGE_E) promo = modelGroup.clubPackE;
  if (INSURANCE_PACKAGE_G) promo = modelGroup.packG;
  if (CLUB && INSURANCE_PACKAGE_G) promo = modelGroup.clubPackG;

  return promo || modelGroup.default;
};

/**
 * Get base promo model
 * @returns IRate | null
 */
export const getPromoModel = (alwaysSelectClub = false): IRate | null => {
  const promoModels = getPromoValues()?.base;
  if (!promoModels) {
    return null;
  }

  return formatPromo(getPromoWithServices(promoModels, alwaysSelectClub));
};

/**
 * Get promo upgrade model
 * @returns IRate | null
 */
export const getPromoUpgradeModel = (alwaysSelectClub = false): IRate | null => {
  const promoValues = getPromoValues();
  const promoModels = promoValues?.base;
  const upgradeModels = promoValues?.upgrade;
  if (!promoModels?.default || !upgradeModels?.default) return null;

  const model = getPromoWithServices(upgradeModels, alwaysSelectClub);

  const PROMO_TYPE_RATA =
    promoModels.default.rate != upgradeModels.default.rate &&
    promoModels.default.imp_finanz === upgradeModels.default.imp_finanz;
  model.type = PROMO_TYPE_RATA ? "rata" : "importo";

  return formatPromo(model);
};

/**
 * Get promoKo model
 */
export const getModelPromoKO = (): IRate | null => {
  const promoModels = getPromoValues()?.base;
  if (!promoModels || !promoModels.ko) return null;
  return formatPromo(promoModels.ko);
};

/**
 * Get promo disclaimer message if not promo available
 */
export function promoDisclaimerMessage() {
  return isFeevo() && isPromoSelected() && !promoAvailable();
}

/**
 * Check if promo available
 */
export const promoAvailable = (): boolean => {
  if (!isPromoSelected()) return false;

  if(getStateValue('isRevolving')) return false;

  // const employmentType = getStateValue("employmentType");
  // const hiringType = getStateValue("hiringType");
  // const housingType = getStateValue("housingType");

  const RISK = getStateValue("financialProblems");
  // const REDDITO = getStateValue("monthlyIncome", 0);
  // const DIPENDENTE_INDETERMINATO =
  //   [JOB_TYPES.DIPENDENTE].includes(employmentType) &&
  //   hiringType == HIRING_TYPES.INDETERMINATO;
  // const PROPRIETARIO_CASA = housingType == HOUSING_TYPES.PROPRIETARIO;
  
  return isFeevo() && !RISK
}

/**
 * Check if given credit is promo
 */
export const isPromo = (credit: number, rate: number | null = null): boolean => {
  if (rate) return !!data.promo?.values.find(model => {
    return model.base.default.imp_finanz === credit && model.base.default.rate === rate
  })
  return !!data.promo?.values.find(model => model.base.default.imp_finanz === credit)
}

/**
 * Check if given credit is a powercredit promo
 */
export const isPowerPromo = (credit: number): boolean => {
  return !!data.promo?.values.find(model => model.base.default.imp_finanz === credit && model.base.default.fido)
}